import React from 'react';
import Header from '../components/Header';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { usePageMeta } from '../utils/usePageMeta';

const AboutPage = () => {
  const { t, language } = useLanguage();
  const { items } = useCart();
  const hasCartItems = items.length > 0;

  usePageMeta({
    title: t('aboutPage.metaTitle'),
    description: t('aboutPage.metaDescription'),
    path: '/about',
    lang: language,
  });

  return (
    <div className="min-h-screen bg-white">
      <Header title={t('header.about')} showBack />

      <main className={`px-4 py-6 max-w-[600px] mx-auto animate-fade-in ${hasCartItems ? 'pb-[100px]' : 'pb-10'}`}>
        <div className="text-center mb-6">
          <h1 className="text-[26px] font-bold text-black m-0 mb-2">{t('aboutPage.title')}</h1>
          <p className="text-base text-dewalt font-semibold m-0">{t('aboutPage.subtitle')}</p>
        </div>

        <p className="text-base text-gray-600 leading-[1.6] m-0 mb-8 text-center">{t('aboutPage.description')}</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-black m-0 mb-5 text-center">{t('aboutPage.whyUsTitle')}</h2>
          {/* Mobile < 400px: 1 column, 400px+: 2 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-100 rounded-2xl p-5 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="w-12 h-12 bg-dewalt rounded-full flex items-center justify-center mx-auto mb-3 text-black">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="text-[15px] font-semibold text-black m-0 mb-1.5">{t('aboutPage.why1Title')}</h3>
              <p className="text-[13px] text-gray-500 m-0 leading-snug">{t('aboutPage.why1Desc')}</p>
            </div>

            <div className="bg-gray-100 rounded-2xl p-5 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="w-12 h-12 bg-dewalt rounded-full flex items-center justify-center mx-auto mb-3 text-black">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="text-[15px] font-semibold text-black m-0 mb-1.5">{t('aboutPage.why2Title')}</h3>
              <p className="text-[13px] text-gray-500 m-0 leading-snug">{t('aboutPage.why2Desc')}</p>
            </div>

            <div className="bg-gray-100 rounded-2xl p-5 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="w-12 h-12 bg-dewalt rounded-full flex items-center justify-center mx-auto mb-3 text-black">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="text-[15px] font-semibold text-black m-0 mb-1.5">{t('aboutPage.why3Title')}</h3>
              <p className="text-[13px] text-gray-500 m-0 leading-snug">{t('aboutPage.why3Desc')}</p>
            </div>

            <div className="bg-gray-100 rounded-2xl p-5 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="w-12 h-12 bg-dewalt rounded-full flex items-center justify-center mx-auto mb-3 text-black">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="text-[15px] font-semibold text-black m-0 mb-1.5">{t('aboutPage.why4Title')}</h3>
              <p className="text-[13px] text-gray-500 m-0 leading-snug">{t('aboutPage.why4Desc')}</p>
            </div>
          </div>
        </section>

        <section className="bg-[#fffbf0] border-2 border-dewalt rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-black m-0 mb-4 text-center">{t('aboutPage.contactTitle')}</h2>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 text-[15px] text-gray-700">
              <svg className="flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#FEBD17"/>
              </svg>
              <span>{t('aboutPage.address')}</span>
            </div>
            <div className="flex items-center gap-3 text-[15px] text-gray-700">
              <svg className="flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#FEBD17" strokeWidth="2"/>
                <path d="M22 6l-10 7L2 6" stroke="#FEBD17" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{t('aboutPage.email')}</span>
            </div>
            <div className="flex items-center gap-3 text-[15px] text-gray-700">
              <svg className="flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="#FEBD17" strokeWidth="2"/>
              </svg>
              <span>{t('aboutPage.phone')}</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AboutPage;
